import { NgZone } from '@angular/core';
import { DataSource, ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollRepeater, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Contact } from '../models/contacts';

export const CONTACT_ROW_HEIGHT = 52;

export type ContactRow = Contact & { rowIndex: number };

export class ContactsVirtualScrollDataSource
  extends DataSource<ContactRow>
  implements CdkVirtualScrollRepeater<ContactRow>
{
  readonly dataStream: Observable<readonly ContactRow[]>;

  private readonly rows$ = new BehaviorSubject<ContactRow[]>([]);
  private readonly disconnected$ = new Subject<void>();
  // CdkTable calls connect() during its own ngOnInit, which runs before this component's
  // @ViewChild(CdkVirtualScrollViewport) setter fires (that resolves in the parent's
  // ngAfterViewInit, after all descendant ngOnInit hooks). A plain nullable field checked
  // once inside connect() would capture "no viewport yet" permanently, since CdkTable
  // subscribes to whatever Observable connect() returns exactly once. Buffering the viewport
  // through a ReplaySubject and switchMap-ing into it makes connect() correct regardless of
  // which happens first.
  private readonly viewport$ = new ReplaySubject<CdkVirtualScrollViewport>(1);

  constructor(private readonly ngZone: NgZone) {
    super();
    this.dataStream = this.rows$.asObservable();
  }

  setData(contacts: Contact[]) {
    this.rows$.next(contacts.map((contact, rowIndex) => ({ ...contact, rowIndex })));
  }

  attachViewport(viewport: CdkVirtualScrollViewport) {
    viewport.attach(this);
    this.viewport$.next(viewport);
  }

  // Required by CdkVirtualScrollRepeater, but unreachable today: the viewport is configured with
  // [itemSize] (contacts.component.html), which uses FixedSizeVirtualScrollStrategy and never
  // calls this — only AutoSizeVirtualScrollStrategy does.
  measureRangeSize(range: ListRange): number {
    return (range.end - range.start) * CONTACT_ROW_HEIGHT;
  }

  connect(): Observable<readonly ContactRow[]> {
    return this.emitInAngularZone(
      this.viewport$.pipe(
        switchMap(viewport =>
          combineLatest([this.rows$, viewport.renderedRangeStream.pipe(startWith({ start: 0, end: 0 }))])
        ),
        map(([rows, range]) => rows.slice(range.start, range.end)),
        takeUntil(this.disconnected$)
      )
    );
  }

  disconnect() {
    this.disconnected$.next();
    this.disconnected$.complete();
  }

  // The viewport registers its scroll listener, and subscribes to dataStream, from inside
  // ngZone.runOutsideAngular() — see CdkVirtualScrollViewport.attach() ("Run outside the zone to
  // avoid triggering change detection, since we're managing the change detection loop ourselves")
  // and the ScrollDispatcher's own listener. So every renderedRangeStream emission caused by a
  // scroll arrives OUTSIDE the Angular zone, and CdkTable renders its rows straight off the
  // stream connect() returns.
  //
  // zone.js captures whichever zone is current at the moment addEventListener runs, so without
  // this wrapper every click/hover handler on a row rendered during a scroll is bound outside the
  // Angular zone: the handler still runs and still mutates component state, but no change
  // detection follows, so nothing appears to happen until some unrelated in-zone event ticks it.
  // This was first diagnosed on the Organisations grid, which mirrors this data source — see
  // organisations-virtual-scroll.data-source.ts and VIRTUAL_SCROLL.md "Gotcha #3".
  private emitInAngularZone<T>(source: Observable<T>): Observable<T> {
    return new Observable<T>(subscriber =>
      source.subscribe({
        next: value => this.runInAngularZone(() => subscriber.next(value)),
        error: (error: unknown) => this.runInAngularZone(() => subscriber.error(error)),
        complete: () => this.runInAngularZone(() => subscriber.complete())
      })
    );
  }

  // Only pays for ngZone.run() when the emission actually came from outside — setData() and the
  // first render already run in the zone, and re-entering a zone you're already in would queue a
  // redundant change-detection tick per emission.
  private runInAngularZone(action: () => void) {
    if (NgZone.isInAngularZone()) {
      action();
      return;
    }
    this.ngZone.run(action);
  }
}
